import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CompanionArcadeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCardSelection: () => void;
  onSlotMachine: () => void;
}

const CompanionArcadeModal: React.FC<CompanionArcadeModalProps> = ({
  isOpen,
  onClose,
  onCardSelection,
  onSlotMachine
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence key="companion-arcade-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 rounded-2xl border-2 border-purple-500/50 shadow-2xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-purple-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🎮</span>
                <h2 className="text-2xl font-bold text-white">Arcade</h2>
              </div>
              <motion.button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-2xl transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                ✕
              </motion.button>
            </div>
            <p className="text-gray-300 mt-2 text-sm">
              Wähle dein Lieblingsspiel aus der Arcade!
            </p>
          </div>

          {/* Arcade Options */}
          <div className="p-6 space-y-4">
            {/* Kartenwahl Option */}
            <motion.button
              onClick={() => {
                onCardSelection();
                onClose();
              }}
              className="w-full h-24 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-xl border border-purple-500/30 hover:border-purple-400/50 transition-all duration-200 group"
              whileHover={{ scale: 1.02, backgroundColor: 'rgba(147, 51, 234, 0.3)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-4 h-full px-6">
                <div className="text-4xl group-hover:scale-110 transition-transform duration-200">
                  🎴
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-bold text-white">Kartenwahl</h3>
                  <p className="text-gray-300 text-sm">
                    Ziehe Karten und gewinne mächtige Begleiter!
                  </p>
                </div>
              </div>
            </motion.button>

            {/* Glücksautomat Option */}
            <motion.button
              onClick={() => {
                onSlotMachine();
                onClose();
              }}
              className="w-full h-24 bg-gradient-to-r from-gray-600/20 to-gray-700/20 rounded-xl border border-gray-500/30 hover:border-gray-400/50 transition-all duration-200 group"
              whileHover={{ scale: 1.02, backgroundColor: 'rgba(75, 85, 99, 0.3)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-4 h-full px-6">
                <div className="text-4xl group-hover:scale-110 transition-transform duration-200">
                  🎰
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-bold text-white">Glücksautomat</h3>
                  <p className="text-gray-300 text-sm">
                    Versuche dein Glück am Slot Machine!
                  </p>
                </div>
              </div>
            </motion.button>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-purple-500/30">
            <motion.button
              onClick={onClose}
              className="w-full py-3 px-6 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg font-medium hover:from-gray-500 hover:to-gray-600 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Schließen
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CompanionArcadeModal; 